import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Upload, KeyRound, Ban, CheckCircle2, Trash2, Users } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { AdminFilterBar, AdminSearchInput, AdminFilterSelect } from "@/components/admin/AdminFilterBar";
import { AdminTable, AdminTableRow, AdminTableCell, AdminTableMessageRow } from "@/components/admin/AdminTable";
import AdminBadge from "@/components/admin/AdminBadge";
import AdminButton from "@/components/admin/AdminButton";
import AdminModal from "@/components/admin/AdminModal";
import AdminEmptyState from "@/components/admin/AdminEmptyState";

const ROLES = ["student", "parent", "teacher", "admin", "supervisor"];

// Each role has its own colour in the users table.
const ROLE_TONE = {
  admin: "bg-hv-coral border-hv-coral text-white",
  parent: "bg-[#E3EEFB] border-[#BFD6F2]",
  student: "bg-white border-[#E5DFC8]",
  teacher: "bg-[#F1E8FB] border-[#D6C2F0]",
  principal: "bg-[#FBF3D6] border-[#EFDC9A]",
  default: "bg-hv-tealTint border-[#E5DFC8]",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("email,name,role,age,language,parent_contact,password\n");
  const [createForm, setCreateForm] = useState({ email: "", name: "", role: "student", language: "en" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("role", role);
      const { data } = await client.get(`/admin/users?${params.toString()}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, role]); // eslint-disable-line

  const createUser = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      const { data } = await client.post("/admin/users", createForm);
      setMsg(data.generated_password
        ? `Created ${data.user.email}. Generated password: ${data.generated_password}`
        : `Created ${data.user.email}.`);
      setShowCreate(false);
      setCreateForm({ email: "", name: "", role: "student", language: "en" });
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const changeRole = async (id, newRole) => {
    try {
      await client.patch(`/admin/users/${id}/role`, { role: newRole });
      setMsg("Role updated.");
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const resetPassword = async (id, email) => {
    if (!window.confirm(`Generate a new password for ${email}?`)) return;
    try {
      const { data } = await client.post(`/admin/users/${id}/reset-password`, {});
      setMsg(`New password for ${email}: ${data.generated_password}`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const setActive = async (id, isActive) => {
    try {
      await client.post(`/admin/users/${id}/set-active`, { is_active: isActive });
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const setMembership = async (id, paid) => {
    try {
      await client.patch(`/admin/users/${id}`, { membership_paid: paid });
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try {
      await client.delete(`/admin/users/${id}`);
      setMsg(`Deleted ${email}.`);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const bulkImport = async () => {
    setErr(""); setMsg("");
    try {
      const { data } = await client.post("/admin/users/bulk-import", { csv: bulkCsv });
      setMsg(`Bulk import: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped.`);
      setShowBulk(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div data-testid="admin-users">
      <AdminPageHeader
        title="User Management"
        subtitle={`${total} account${total === 1 ? "" : "s"}`}
        actions={
          <>
            <AdminButton variant="primary" icon={UserPlus} onClick={() => setShowCreate(true)} data-testid="admin-users-create-toggle">
              Add user
            </AdminButton>
            <AdminButton variant="secondary" icon={Upload} onClick={() => setShowBulk(true)} data-testid="admin-users-bulk-toggle">
              Bulk upload (CSV)
            </AdminButton>
          </>
        }
      />

      <AdminFilterBar>
        <AdminSearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" testId="admin-users-search" />
        <AdminFilterSelect label="Role" value={role} onChange={(e) => setRole(e.target.value)} testId="admin-users-role-filter">
          <option value="">All</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </AdminFilterSelect>
      </AdminFilterBar>

      {err && <div className="mt-4 text-hv-coral font-bold text-sm" data-testid="admin-users-error">{err}</div>}
      {msg && <div className="mt-4 text-hv-teal font-bold text-sm break-all" data-testid="admin-users-msg">{msg}</div>}

      <AdminModal open={showCreate} onClose={() => setShowCreate(false)} title="Add user" testId="admin-users-create-form">
        <form onSubmit={createUser} className="space-y-3">
          <input required placeholder="Email" value={createForm.email}
                 onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                 className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm" />
          <input required placeholder="Name" value={createForm.name}
                 onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                 className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm" />
          <select value={createForm.role} onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="Password (leave blank to generate)" value={createForm.password || ""}
                 onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                 className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm" />
          <div className="flex justify-end gap-2 pt-2">
            <AdminButton type="button" variant="secondary" onClick={() => setShowCreate(false)}>Cancel</AdminButton>
            <AdminButton type="submit" variant="primary">Create</AdminButton>
          </div>
        </form>
      </AdminModal>

      <AdminModal open={showBulk} onClose={() => setShowBulk(false)} title="Bulk upload (CSV)" maxWidth="max-w-2xl" testId="admin-users-bulk-form">
        <p className="text-hv-slate text-sm">CSV header: email,name,role,age,language,parent_contact,password. Only email is required.</p>
        <textarea rows={8} value={bulkCsv} onChange={(e) => setBulkCsv(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] font-mono text-xs" />
        <div className="flex justify-end gap-2 pt-2">
          <AdminButton variant="secondary" onClick={() => setShowBulk(false)}>Cancel</AdminButton>
          <AdminButton variant="primary" onClick={bulkImport}>Import</AdminButton>
        </div>
      </AdminModal>

      {loading ? (
        <p className="mt-6 text-hv-slate text-sm">Loading…</p>
      ) : users.length === 0 ? (
        <div className="mt-6">
          <AdminEmptyState icon={Users} title="No users found." />
        </div>
      ) : (
        <div className="mt-6">
          <AdminTable
            testId="admin-users-list"
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "role", label: "Role" },
              { key: "status", label: "Status" },
              { key: "membership", label: "Membership" },
              { key: "actions", label: "Actions", align: "right" },
            ]}
          >
            {users.map((u) => (
              <AdminTableRow key={u.id}>
                <AdminTableCell><span className="font-semibold text-hv-navy">{u.name || "—"}</span></AdminTableCell>
                <AdminTableCell><span className="text-hv-slate">{u.email}</span></AdminTableCell>
                <AdminTableCell>
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={u.id === me?.id}
                          data-testid={`admin-users-role-${u.id}`}
                          className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-hv-navy ${ROLE_TONE[u.role] || ROLE_TONE.default}`}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </AdminTableCell>
                <AdminTableCell>
                  {u.is_active === false
                    ? <AdminBadge tone="destructive">Deactivated</AdminBadge>
                    : <AdminBadge tone="success">Active</AdminBadge>}
                </AdminTableCell>
                <AdminTableCell>
                  <button onClick={() => setMembership(u.id, !u.membership_paid)} data-testid={`admin-users-membership-${u.id}`}>
                    {u.membership_paid
                      ? <AdminBadge tone="success">Paid</AdminBadge>
                      : <AdminBadge>Free (first month)</AdminBadge>}
                  </button>
                </AdminTableCell>
                <AdminTableCell align="right">
                  <div className="flex items-center justify-end gap-1">
                    <AdminButton iconOnly icon={KeyRound} title="Reset password" onClick={() => resetPassword(u.id, u.email)} />
                    {u.is_active === false ? (
                      <AdminButton iconOnly icon={CheckCircle2} title="Reactivate" onClick={() => setActive(u.id, true)} />
                    ) : (
                      <AdminButton iconOnly icon={Ban} title="Deactivate" disabled={u.id === me?.id} onClick={() => setActive(u.id, false)} />
                    )}
                    <AdminButton iconOnly icon={Trash2} title="Delete" disabled={u.id === me?.id} onClick={() => remove(u.id, u.email)} />
                  </div>
                </AdminTableCell>
              </AdminTableRow>
            ))}
          </AdminTable>
        </div>
      )}
    </div>
  );
}
